/**
 * Data plane — Knowledge graph, FOCUSED view (L2, ADR-0026 §4).
 *
 * A full node-link graph of every dataset+edge is a hairball no layout can tame.
 * Instead this is an **ego view**: a left list of datasets (grouped by owner) +
 * a canvas that shows ONE selected dataset at the centre with only its DIRECT
 * relationships — incoming on the left ("referenced by"), outgoing on the right
 * ("references"). A star never overlaps; click a neighbour to walk the graph.
 * Deferred test tier (ReactFlow), like AppErDiagram / MapView.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Bot, Database, Loader2, Network, Plus, ScanSearch, Search } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ReactFlow, {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";

import { DiscoverRelationshipsDialog } from "@/components/datasets/DiscoverRelationshipsDialog";
import { ProposeAiRelationshipsDialog } from "@/components/datasets/ProposeAiRelationshipsDialog";
import { RelationshipDialog } from "@/components/datasets/RelationshipDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { cn } from "@/lib/cn";
import { FeatureTip } from "@/components/billing/FeatureGate";
import { datasetsApi, type Dataset } from "@/lib/api-datasets";
import {
  CARDINALITY_GLYPH,
  relationshipsApi,
  type Relationship,
} from "@/lib/api-relationships";

const CENTER_X = 380;
const SIDE_GAP = 360; // horizontal distance centre → neighbour column
const ROW = 96; // vertical gap between stacked neighbours

/** Build the ego star for `sel`: centre node + its direct relationships, incoming
 *  neighbours on the left, outgoing on the right. No two ever overlap. */
function buildEgo(
  sel: string,
  selLabel: { name: string; owner: string },
  rels: Relationship[],
): { nodes: Node[]; edges: Edge[] } {
  const outgoing = rels.filter((r) => r.from_dataset_id === sel);
  const incoming = rels.filter((r) => r.to_dataset_id === sel && r.from_dataset_id !== sel);
  const rows = Math.max(incoming.length, outgoing.length, 1);
  const midY = ((rows - 1) * ROW) / 2;

  const nodeStyle = (accent: boolean): React.CSSProperties => ({
    border: accent ? "1.5px solid hsl(var(--accent))" : "1px solid hsl(var(--border))",
    background: accent ? "hsl(var(--elevated))" : "hsl(var(--surface))",
    color: "hsl(var(--fg))",
    borderRadius: 10,
    fontSize: 12,
    padding: "8px 12px",
    width: 180,
  });
  const label = (owner: string, name: string) => ({
    label: (
      <div className="leading-tight">
        <div className="truncate text-[10px] font-normal text-muted-foreground">{owner}</div>
        <div className="truncate font-medium">{name}</div>
      </div>
    ),
  });

  const nodes: Node[] = [
    {
      id: `c:${sel}`,
      position: { x: CENTER_X, y: midY },
      data: label(selLabel.owner, selLabel.name),
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      draggable: false,
      style: nodeStyle(true),
    },
  ];
  const edges: Edge[] = [];

  const edgeProps = (r: Relationship) => ({
    id: r.id,
    type: "smoothstep" as const,
    label: `${CARDINALITY_GLYPH[r.cardinality]}  ${r.from_field} → ${r.to_field}`,
    labelStyle: { fill: "hsl(var(--fg))", fontSize: 10, fontWeight: 500 },
    labelBgStyle: { fill: "hsl(var(--elevated))" },
    labelBgPadding: [6, 3] as [number, number],
    labelBgBorderRadius: 4,
    markerEnd: { type: MarkerType.ArrowClosed, color: "hsl(var(--muted-fg))", width: 16, height: 16 },
    style: { stroke: "hsl(var(--muted-fg))", strokeWidth: 1.5 },
  });

  incoming.forEach((r, i) => {
    const nid = `n:${r.id}`;
    nodes.push({
      id: nid,
      position: { x: CENTER_X - SIDE_GAP, y: midY - ((incoming.length - 1) * ROW) / 2 + i * ROW },
      data: { ...label(r.from_dataset_name, r.from_dataset_name), datasetId: r.from_dataset_id },
      sourcePosition: Position.Right,
      draggable: false,
      style: nodeStyle(false),
    });
    edges.push({ ...edgeProps(r), source: nid, target: `c:${sel}` });
  });

  outgoing.forEach((r, i) => {
    const nid = `n:${r.id}`;
    nodes.push({
      id: nid,
      position: { x: CENTER_X + SIDE_GAP, y: midY - ((outgoing.length - 1) * ROW) / 2 + i * ROW },
      data: { ...label(r.to_dataset_name, r.to_dataset_name), datasetId: r.to_dataset_id },
      targetPosition: Position.Left,
      draggable: false,
      style: nodeStyle(false),
    });
    edges.push({ ...edgeProps(r), source: `c:${sel}`, target: nid });
  });

  return { nodes, edges };
}

export function DatasetGraphPage() {
  const { t } = useTranslation();
  const datasetsQ = useQuery({ queryKey: ["datasets", "list"], queryFn: () => datasetsApi.list() });
  const relsQ = useQuery({ queryKey: ["relationships", "all"], queryFn: () => relationshipsApi.list() });

  const [selected, setSelected] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [relOpen, setRelOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Relationship | null>(null);
  const [discoverOpen, setDiscoverOpen] = React.useState(false);
  const [aiOpen, setAiOpen] = React.useState(false);

  const datasets = React.useMemo(() => datasetsQ.data ?? [], [datasetsQ.data]);
  const rels = React.useMemo(() => relsQ.data ?? [], [relsQ.data]);
  const byId = React.useMemo(() => new Map(datasets.map((d) => [d.id, d])), [datasets]);

  // Degree per dataset (count of relationships touching it) — shown in the list.
  const degree = React.useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rels) {
      m.set(r.from_dataset_id, (m.get(r.from_dataset_id) ?? 0) + 1);
      m.set(r.to_dataset_id, (m.get(r.to_dataset_id) ?? 0) + 1);
    }
    return m;
  }, [rels]);

  // Default to the most-connected dataset once data loads.
  React.useEffect(() => {
    if (selected || datasets.length === 0) return;
    const best = [...datasets].sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0))[0];
    setSelected(best?.id ?? null);
  }, [datasets, degree, selected]);

  // Left list: datasets grouped by owner (app / source), filtered by search.
  const groups = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = datasets.filter(
      (d) => !q || d.name.toLowerCase().includes(q) || d.owner.toLowerCase().includes(q),
    );
    const byOwner = new Map<string, Dataset[]>();
    for (const d of filtered) (byOwner.get(d.owner) ?? byOwner.set(d.owner, []).get(d.owner)!).push(d);
    return [...byOwner.entries()]
      .map(([owner, ds]) => ({ owner, datasets: ds.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.owner.localeCompare(b.owner));
  }, [datasets, search]);

  const selDs = selected ? byId.get(selected) : undefined;
  const ego = React.useMemo(() => {
    if (!selDs) return { nodes: [] as Node[], edges: [] as Edge[] };
    const touching = rels.filter(
      (r) => r.from_dataset_id === selDs.id || r.to_dataset_id === selDs.id,
    );
    return buildEgo(selDs.id, { name: selDs.name, owner: selDs.owner }, touching);
  }, [selDs, rels]);

  const onNodeClick = React.useCallback((_: unknown, node: Node) => {
    const dsId = (node.data as { datasetId?: string } | undefined)?.datasetId;
    if (dsId) setSelected(dsId);
  }, []);
  const onEdgeClick = React.useCallback((_: unknown, edge: Edge) => {
    relationshipsApi.get(edge.id).then((r) => {
      setEditing(r);
      setRelOpen(true);
    });
  }, []);

  const loading = datasetsQ.isLoading || relsQ.isLoading;
  const selDegree = selected ? (degree.get(selected) ?? 0) : 0;

  return (
    <PageContainer>
      <PageHeader
        title={t("relationships.graphTitle", "Grafo dati")}
        description={t(
          "relationships.graphSubtitle",
          "Scegli un dataset per vedere le sue relazioni dirette. Clicca un vicino per navigare.",
        )}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/datasets">
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                {t("common.back", "Indietro")}
              </Link>
            </Button>
            <Button variant="outline" size="sm" onClick={() => setDiscoverOpen(true)}>
              <ScanSearch className="h-4 w-4" strokeWidth={1.5} />
              {t("relationships.discover.title", "Scopri relazioni")}
            </Button>
            <FeatureTip feature="ai_plane">
              <Button variant="outline" size="sm" onClick={() => setAiOpen(true)}>
                <Bot className="h-4 w-4" strokeWidth={1.5} />
                {t("relationships.proposeAi.action", "Proponi con AI")}
              </Button>
            </FeatureTip>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setRelOpen(true);
              }}
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("relationships.add", "Relazione")}
            </Button>
          </div>
        }
      />
      <PageContent className="flex min-h-0 flex-col">
        <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-border/60">
          {/* Left: dataset list grouped by owner */}
          <div className="flex w-72 shrink-0 flex-col border-r border-border/60 bg-surface/40">
            <div className="border-b border-border/60 p-2">
              <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-2.5">
                <Search className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("relationships.focus.search", "Cerca dataset…")}
                  className="h-8 w-full bg-transparent text-sm focus:outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto p-1.5">
              {loading ? (
                <div className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
                  {t("common.loading", "Caricamento…")}
                </div>
              ) : groups.length === 0 ? (
                <p className="p-3 text-center text-xs text-muted-foreground">
                  {t("relationships.focus.noDatasets", "Nessun dataset.")}
                </p>
              ) : (
                groups.map((grp) => (
                  <div key={grp.owner} className="mb-2">
                    <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {grp.owner}
                    </div>
                    {grp.datasets.map((d) => {
                      const deg = degree.get(d.id) ?? 0;
                      const active = d.id === selected;
                      return (
                        <button
                          key={d.id}
                          onClick={() => setSelected(d.id)}
                          className={cn(
                            "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm",
                            active ? "bg-accent/15 text-foreground" : "text-muted-foreground hover:bg-surface",
                          )}
                        >
                          <Database className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                          <span className="flex-1 truncate">{d.name}</span>
                          {deg > 0 && (
                            <Badge tone={active ? "active" : "neutral"} className="text-[10px]">
                              {deg}
                            </Badge>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right: ego star of the selected dataset */}
          <div className="relative flex-1">
            {!selDs ? (
              <EmptyState
                icon={Network}
                title={t("relationships.focus.pick", "Scegli un dataset")}
                description={t(
                  "relationships.focus.pickDescription",
                  "Seleziona un dataset a sinistra per vedere le sue relazioni.",
                )}
              />
            ) : selDegree === 0 ? (
              <EmptyState
                icon={Network}
                title={t("relationships.focus.none", "Nessuna relazione")}
                description={t(
                  "relationships.focus.noneDescription",
                  "Questo dataset non ha ancora relazioni. Usa Scopri, Proponi con AI o crea una relazione.",
                )}
              />
            ) : (
              <>
                <div className="pointer-events-none absolute left-3 top-3 z-10 text-[11px] font-medium text-muted-foreground">
                  ← {t("relationships.focus.referencedBy", "Referenziato da")}
                </div>
                <div className="pointer-events-none absolute right-3 top-3 z-10 text-[11px] font-medium text-muted-foreground">
                  {t("relationships.focus.references", "Referenzia")} →
                </div>
                <ReactFlowProvider>
                  <ReactFlow
                    nodes={ego.nodes}
                    edges={ego.edges}
                    onNodeClick={onNodeClick}
                    onEdgeClick={onEdgeClick}
                    nodesConnectable={false}
                    fitView
                    fitViewOptions={{ padding: 0.25 }}
                    proOptions={{ hideAttribution: true }}
                  >
                    <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
                    <Controls showInteractive={false} />
                  </ReactFlow>
                </ReactFlowProvider>
              </>
            )}
          </div>
        </div>
      </PageContent>

      <RelationshipDialog
        open={relOpen}
        onOpenChange={setRelOpen}
        fromDatasetId={editing?.from_dataset_id ?? selected ?? ""}
        editing={editing}
      />
      <DiscoverRelationshipsDialog open={discoverOpen} onOpenChange={setDiscoverOpen} />
      <ProposeAiRelationshipsDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        datasetId={selected ?? undefined}
      />
    </PageContainer>
  );
}
